/**
 * Azure (Static Web App / build de despliegue).
 *
 * - IdP: Microsoft Entra ID (App Registration SPA `app-dev-matrizreca-spa`).
 * - API: APIM DESA único — `https://api-apim.dev.colmena.cl/{slug}/api/v1/...`
 *   (ej. health seguridad: `…/ingresos-cotizaciones-seguridad/api/v1/health`).
 * - Permisos admin sin grupos JWT: **seguridad-api** App Service:
 *     AUTH_GRANT_ADMIN_WITHOUT_GROUPS=true
 *
 * - CI rama `develop` (SWA DEV cliente): build config `development` → este archivo.
 * - CI ramas `release/*` (SWA QA): build config `qa` → `environment.qa.ts`
 *   (App Registration `app-qa-matrizreca-spa`).
 * - Desarrollo local Keycloak + proxy :7234→:7235: `environment.local.ts`
 *   (`ng serve` / `npm start` con config `oidc-dev` en `angular.json`).
 */
import type { ColmenaApiService, Environment } from './environment.model';

/**
 * Microsoft Entra ID (Azure AD) — App Registration SPA `app-dev-matrizreca-spa`.
 * - Application (Client) ID: 551b73f2-581d-4d97-b9d8-d25e11a76bb4
 * - Directory (Tenant) ID:   c33c19b8-e1b6-414f-a61b-07fda5c6ad45
 */
const ENTRA_TENANT_ID = 'c33c19b8-e1b6-414f-a61b-07fda5c6ad45';
const ENTRA_CLIENT_ID = '551b73f2-581d-4d97-b9d8-d25e11a76bb4';

/** APIM DESA — una sola base; cada MS es un slug bajo este host. */
const APIM_BASE = 'https://api-apim.dev.colmena.cl';

/**
 * Slug de path APIM por microservicio (`{APIM_BASE}/{slug}/api/v1/...`).
 * Alineado a `TF_VAR_path` de cada MS en DESA (todos con prefijo `ingresos-cotizaciones-`).
 */
const APIM_SERVICE_SLUGS: Record<ColmenaApiService, string> = {
  seguridad: 'ingresos-cotizaciones-seguridad',
  catalogos: 'ingresos-cotizaciones-catalogos',
  ingesta: 'ingresos-cotizaciones-ingesta',
  reglas: 'ingresos-cotizaciones-reglas',
  confirmacionPago: 'ingresos-cotizaciones-confirmacion-pago',
  matriz: 'ingresos-cotizaciones-matriz',
  auditoria: 'ingresos-cotizaciones-auditoria',
  consultas: 'ingresos-cotizaciones-consultas',
};

/** Origen APIM por MS: base + slug (sin barra final). */
function apimServiceOrigin(service: ColmenaApiService): string {
  return `${APIM_BASE}/${APIM_SERVICE_SLUGS[service]}`;
}

const APIM_SERVICE_ORIGINS = Object.fromEntries(
  (Object.keys(APIM_SERVICE_SLUGS) as ColmenaApiService[]).map((service) => [
    service,
    apimServiceOrigin(service),
  ]),
) as NonNullable<Environment['apiServiceOrigins']>;

export const environment: Environment = {
  production: true,
  appBasePath: '',
  apiBaseUrl: APIM_BASE,
  apiPathPrefix: '/api/v1',
  apiPort: 443,
  useApiProxy: false,
  apiRouting: 'per-service',
  apiServiceOrigins: APIM_SERVICE_ORIGINS,
  authDevBypass: false,
  devAuthToken: 'dev-bypass-token',
  keycloak: {
    issuer: `https://login.microsoftonline.com/${ENTRA_TENANT_ID}/v2.0`,
    clientId: ENTRA_CLIENT_ID,
    scope: 'openid profile email offline_access',
  },
};
